import { useEffect, useMemo, useRef, useState } from 'react';
import { getPose } from '../data/poses';
import { formatDuration, stepIsBilateral, type RoutinesApi } from '../hooks/useRoutines';
import type { ProgressApi } from '../hooks/useProgress';
import type { Pose, Routine } from '../types';
import { PoseMedia } from './PoseMedia';
import {
  createWebSpeechAnnouncer,
  silentAnnouncer,
  type RoutineAnnouncer,
} from '../audio/announcer';
import { createClipAnnouncer, defaultClipVoiceId, hasClipVoice } from '../audio/clipAnnouncer';
import { getVoiceChoice } from '../audio/voices';
import { useWakeLock } from '../hooks/useWakeLock';

const AUDIO_PREF_KEY = 'yoga-me.audio.v1';
// Spoken "N seconds to go" fires at each multiple of this within a hold.
const COUNTDOWN_INTERVAL = 10; // seconds
// Final "3, 2, 1" is spoken at the matching clock values (secondsLeft 3/2/1),
// then "And release" at 0. Skipped on very short holds so it doesn't start
// right after "Begin".
const MIN_HOLD_FOR_FINAL = 5;
// Breather after "And release" before the next pose is announced.
const POST_RELEASE_PAUSE_MS = 2000;
// Longer gap before "Begin" on the second side, to turn around and reset.
const SWITCH_PAUSE_MS = 4000;

interface Props {
  routines: RoutinesApi;
  progress: ProgressApi;
  routineId: string;
  /** When set, play this routine directly (e.g. a shared/public one). */
  routine?: Routine;
  /** Where Exit/Done go (default the routines list). */
  exitHref?: string;
}

// A single thing to hold. A two-sided ("2fer") pose expands into two plays:
// its first side, then its second side.
type Side = 'single' | 'first' | 'second';
interface ResolvedStep {
  pose: Pose;
  holdSeconds: number;
  side: Side;
}

export function RoutinePlayerPage({
  routines,
  progress,
  routineId,
  routine: providedRoutine,
  exitHref = '#/routines',
}: Props) {
  const routine = providedRoutine ?? routines.get(routineId);

  const steps: ResolvedStep[] = useMemo(() => {
    if (!routine) return [];
    const out: ResolvedStep[] = [];
    for (const s of routine.steps) {
      const pose = getPose(s.poseId);
      if (!pose) continue;
      if (stepIsBilateral(s)) {
        out.push({ pose, holdSeconds: s.holdSeconds, side: 'first' });
        out.push({ pose, holdSeconds: s.holdSeconds, side: 'second' });
      } else {
        out.push({ pose, holdSeconds: s.holdSeconds, side: 'single' });
      }
    }
    return out;
  }, [routine]);

  const [index, setIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(() => steps[0]?.holdSeconds ?? 0);
  const [paused, setPaused] = useState(false);
  const [finished, setFinished] = useState(false);
  const [marked, setMarked] = useState(false);
  // The hold clock is frozen (running === false) while a pose's intro is being
  // announced, then starts when "Begin" lands. See the announce/arm effect.
  const [running, setRunning] = useState(false);

  // Audio cues. The announcer is created once; a null result means the browser
  // has no speech support, in which case we use the silent no-op and hide the
  // toggle entirely.
  const announcerRef = useRef<RoutineAnnouncer | undefined>(undefined);
  if (announcerRef.current === undefined) {
    // Cues come from pre-rendered clip voices. A saved clip choice uses that
    // voice; anything else (Automatic, or a legacy system-voice pref) falls back
    // to the default clip voice. Web Speech is only a last resort if no clip
    // voices exist at all.
    const choice = getVoiceChoice();
    const clipVoiceId =
      choice.kind === 'clip' && hasClipVoice(choice.id) ? choice.id : defaultClipVoiceId;
    announcerRef.current =
      (clipVoiceId ? createClipAnnouncer(clipVoiceId) : null) ??
      createWebSpeechAnnouncer() ??
      silentAnnouncer;
  }
  const announcer = announcerRef.current;
  const cuesSupported = announcer !== silentAnnouncer;
  const [audioOn, setAudioOn] = useState(() => localStorage.getItem(AUDIO_PREF_KEY) !== 'off');

  useEffect(() => {
    localStorage.setItem(AUDIO_PREF_KEY, audioOn ? 'on' : 'off');
  }, [audioOn]);

  // Keep the screen awake while performing — audio cues are useless if the
  // device sleeps mid-routine.
  useWakeLock(!finished && steps.length > 0);

  // Practice mode: sizes the player to the viewport (and drops the site chrome
  // on phones) so nothing scrolls mid-hold. See `body.playing` in index.css.
  // Owned here rather than by the route, so a shared routine gets it too.
  useEffect(() => {
    if (steps.length === 0) return; // the not-found / empty states keep the site chrome
    document.body.classList.add('playing');
    return () => document.body.classList.remove('playing');
  }, [steps.length]);

  // Warm every clip the routine will need (pose names + "other side") up front,
  // so no pose's title is delayed by first-play fetch/decode latency. Each clip
  // is ~10KB and cached after the first call, so this is cheap and idempotent.
  useEffect(() => {
    if (!audioOn || !cuesSupported || steps.length === 0) return;
    const ids = new Set<string>();
    for (const s of steps) {
      ids.add(s.pose.id);
      if (s.side !== 'single') ids.add('other-side');
    }
    ids.forEach((id) => announcer.prefetch(id));
  }, [audioOn, cuesSupported, steps, announcer]);

  // Total elapsed time for the whole routine — every hold, transition, and
  // spoken cue, i.e. the true wall-clock duration as it progresses. It advances
  // whenever the routine is playing and pauses with Pause; it freezes at the
  // finish so the done screen shows the final time.
  const [elapsedSec, setElapsedSec] = useState(0);
  const accumMsRef = useRef(0);
  const runStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (paused || finished || steps.length === 0) return;
    runStartRef.current = Date.now();
    const tick = () =>
      setElapsedSec(Math.floor((accumMsRef.current + (Date.now() - (runStartRef.current ?? Date.now()))) / 1000));
    tick();
    const id = setInterval(tick, 250);
    return () => {
      clearInterval(id);
      if (runStartRef.current !== null) {
        accumMsRef.current += Date.now() - runStartRef.current; // bank the segment
        runStartRef.current = null;
      }
      setElapsedSec(Math.floor(accumMsRef.current / 1000));
    };
  }, [paused, finished, steps.length]);

  // Arm each pose: freeze the clock, announce it, and start the clock only when
  // the spoken "Begin" lands (or immediately when audio is off — nothing to wait
  // for). Guarded by `running` so resuming mid-hold doesn't re-announce; an
  // intro interrupted by a pause re-announces on resume (running is still false).
  useEffect(() => {
    if (finished || paused || running || steps.length === 0) return;
    const s = steps[index];
    if (!s) return;
    if (audioOn && cuesSupported) {
      const second = s.side === 'second';
      announcer.announceStep(
        {
          name: second ? 'Other side' : s.pose.nameEnglish,
          clipId: second ? 'other-side' : s.pose.id,
          holdSeconds: s.holdSeconds,
          index,
          total: steps.length,
        },
        () => setRunning(true),
        second ? SWITCH_PAUSE_MS : undefined,
      );
    } else {
      setRunning(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, finished, paused, audioOn, running]);

  // Safety net: never let the clock stay frozen forever if a "Begin" cue is lost
  // (speech quirk, cancelled mid-intro, etc.). Cleared as soon as it starts.
  useEffect(() => {
    if (finished || paused || running || steps.length === 0) return;
    const t = setTimeout(() => setRunning(true), 8000);
    return () => clearTimeout(t);
  }, [finished, paused, running, index, steps.length]);

  // Per-second spoken checkpoints during a hold: an every-10s "N seconds to go"
  // countdown for the bulk of the hold, then a final "3, 2, 1, release" over the
  // last 4 seconds. Milestones skip 0 and the very start (secondsLeft === hold).
  useEffect(() => {
    if (!audioOn || finished || paused || !running) return;
    const s = steps[index];
    if (!s) return;
    if (
      secondsLeft >= COUNTDOWN_INTERVAL &&
      secondsLeft % COUNTDOWN_INTERVAL === 0 &&
      secondsLeft !== s.holdSeconds
    ) {
      announcer.announceTimeRemaining(secondsLeft);
    } else if (s.holdSeconds >= MIN_HOLD_FOR_FINAL && secondsLeft === 3) {
      // One smooth "3, 2, 1" over the final 3 seconds. "And release" (at
      // secondsLeft 0) is spoken from the countdown effect so the post-release
      // pause can be timed from when it finishes.
      announcer.announceCountdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, audioOn, finished, paused, running]);

  useEffect(() => {
    if (finished && audioOn) announcer.announceComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, audioOn]);

  // Silence speech on pause, and on unmount/exit.
  useEffect(() => {
    if (paused) announcer.cancel();
  }, [paused, announcer]);
  useEffect(() => () => announcer.cancel(), [announcer]);

  const goTo = (i: number) => {
    setIndex(i);
    setSecondsLeft(steps[i]?.holdSeconds ?? 0);
    setPaused(false);
    setRunning(false); // re-freeze; the next pose's intro re-arms the clock
  };

  // Leave the finished screen and start over from the top. Clearing `finished`
  // is what actually makes Restart work — goTo alone can't, since the whole
  // render (and the countdown effect) is gated on `finished`.
  const restart = () => {
    setFinished(false);
    setMarked(false);
    accumMsRef.current = 0; // reset the total-elapsed stopwatch
    runStartRef.current = null;
    setElapsedSec(0);
    goTo(0);
  };

  // Countdown — reschedules each tick; advances at zero, finishes at the end.
  // Gated on `running` so the clock stays frozen during a pose's spoken intro.
  useEffect(() => {
    if (finished || paused || !running || steps.length === 0) return;
    if (secondsLeft <= 0) {
      // Pose done. Speak "And release", then start the POST_RELEASE_PAUSE only
      // once it has finished — so the full pause is silent, not overlapped by
      // the release cue — before advancing to the next pose.
      let cancelled = false;
      let pauseTimer: ReturnType<typeof setTimeout> | null = null;
      const advance = () => {
        if (cancelled) return;
        if (index < steps.length - 1) goTo(index + 1);
        else setFinished(true);
      };
      // Switching to the same pose's other side needs no post-release breather —
      // the "Other side" cue and its longer switch pause provide the break.
      const switching = steps[index + 1]?.side === 'second';
      const postRelease = switching ? 0 : POST_RELEASE_PAUSE_MS;
      const startPause = () => {
        if (cancelled) return;
        pauseTimer = setTimeout(advance, postRelease);
      };
      const s = steps[index];
      if (audioOn && s && s.holdSeconds >= MIN_HOLD_FOR_FINAL) {
        announcer.announceRelease(startPause); // pause starts when release ends
      } else {
        pauseTimer = setTimeout(advance, audioOn ? postRelease : 0);
      }
      return () => {
        cancelled = true;
        if (pauseTimer) clearTimeout(pauseTimer);
      };
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, paused, running, finished, index, steps.length, audioOn]);

  if (!routine) {
    return (
      <div className="empty">
        <p>Routine not found.</p>
        <a className="btn" href={exitHref}>
          ← Back to routines
        </a>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className="empty">
        <p>This routine has no valid poses.</p>
        <a className="btn" href={`#/routines/${routine.id}/edit`}>
          Edit routine
        </a>
      </div>
    );
  }

  if (finished) {
    const uniquePoseIds = new Set(steps.map((s) => s.pose.id));
    const markPracticed = () => {
      for (const id of uniquePoseIds) {
        if (progress.statusOf(id) === 'new') progress.setStatus(id, 'practiced');
      }
      setMarked(true);
    };
    return (
      <div className="player-done">
        <div className="done-mark" aria-hidden="true">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path
              d="M9 19l6 6L27 12"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h1>Routine complete</h1>
        <p className="muted">
          Nice work — {steps.length} poses, {routine.name}. Total time {formatDuration(elapsedSec)}.
        </p>
        <div className="done-actions">
          <button type="button" className="btn primary" onClick={restart}>
            ↺ Restart
          </button>
          <button type="button" className="btn" onClick={markPracticed} disabled={marked}>
            {marked ? '✓ Marked as practiced' : 'Mark poses practiced'}
          </button>
          <a className="btn" href={exitHref}>
            Done
          </a>
        </div>
        {marked && (
          <p className="muted">
            Saved to your progress — {uniquePoseIds.size} pose
            {uniquePoseIds.size === 1 ? '' : 's'} marked practiced.
          </p>
        )}
      </div>
    );
  }

  const step = steps[index];
  const pct = step.holdSeconds ? (1 - secondsLeft / step.holdSeconds) * 100 : 100;

  return (
    <div className="player" onPointerDown={() => announcer.unlock()}>
      <div className="player-top">
        <a className="back" href={exitHref}>
          ✕ Exit
        </a>
        <div className="player-top-right">
          {cuesSupported && (
            <button
              type="button"
              className={`icon audio-toggle${audioOn ? ' on' : ''}`}
              aria-pressed={audioOn}
              aria-label={audioOn ? 'Mute spoken cues' : 'Enable spoken cues'}
              title={audioOn ? 'Spoken cues on' : 'Spoken cues off'}
              onClick={() => {
                announcer.unlock();
                announcer.cancel();
                setAudioOn((v) => !v);
              }}
            >
              {audioOn ? '🔊' : '🔇'}
            </button>
          )}
          <span className="player-elapsed" title="Total elapsed time" aria-label="Total elapsed time">
            ⏱ {formatDuration(elapsedSec)}
          </span>
          <span className="player-counter">
            {index + 1} / {steps.length}
          </span>
        </div>
      </div>

      <div className="step-bar">
        <span style={{ width: `${pct}%` }} />
      </div>

      <div className="player-media">
        <PoseMedia pose={step.pose} showNumber={false} />
      </div>

      <div className="player-info">
        <h1>{step.pose.nameEnglish}</h1>
        {step.pose.nameSanskrit && <div className="sanskrit lg">{step.pose.nameSanskrit}</div>}
        {step.side !== 'single' && (
          <div className="player-side">
            {step.side === 'first' ? '↔ First side' : '↔ Other side'}
          </div>
        )}
        <div className="countdown">{formatDuration(secondsLeft)}</div>
        {step.pose.cues && step.pose.cues.length > 0 && (
          <ol className="cues">
            {step.pose.cues.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ol>
        )}
      </div>

      <div className="player-controls">
        <button
          type="button"
          className="btn"
          disabled={index === 0}
          onClick={() => goTo(index - 1)}
        >
          ⏮ Prev
        </button>
        <button type="button" className="btn primary wide" onClick={() => setPaused((p) => !p)}>
          {paused ? '▶ Resume' : '⏸ Pause'}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => (index < steps.length - 1 ? goTo(index + 1) : setFinished(true))}
        >
          Next ⏭
        </button>
      </div>
    </div>
  );
}
